/**
 * PromptEditor — the right-hand editor for the currently selected reasoning
 * config key. Fully controlled: the parent (ReasoningPromptsPage) owns the
 * buffer state and dirty computation so it can also drive the discard-guard
 * in ConfigKeyList and the save confirmation copy (which needs the active
 * row's version number).
 */

import React from 'react';
import { Box, Typography, TextField, Button, Alert } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import PromptField from '../../shared/PromptField';
import { colors, typography } from '../../../theme/designTokens';
import { ReasoningConfigKey, ReasoningConfigRow } from '../../../services/quillAdminService';
import { ReasoningEditorBuffer, FieldSupport, fieldsForKey } from './reasoningManifest';

const ACCENT = colors.deepTeal[500];

/**
 * F9: the one-line pointer that replaces an editor the engine does not honour.
 *
 * The editor used to show model / temperature / max_tokens for every key, when
 * which of them does anything differs per key — and `model` is overridden by a
 * pipeline row on every key that has one. An input that looks like it sets the
 * model and does not is the same lie as a save button that doesn't save, so a
 * superseded field becomes a sentence saying where the value really comes from,
 * and an ignored one is not rendered at all.
 *
 * The VALUE is still shown for a superseded field: it is what would be used if
 * the pipeline row ever resolved nothing, and hiding it would make the history
 * drawer's rows unreadable.
 */
const SupersededField: React.FC<{ label: string; value: React.ReactNode; support: FieldSupport }> = ({
  label,
  value,
  support,
}) => {
  if (support.support !== 'superseded') return null;
  return (
    <Box
      data-testid={`superseded-${label.toLowerCase().replace(/\s+/g, '-')}`}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        border: `1px dashed ${colors.gray[300]}`,
        backgroundColor: colors.gray[50],
        minWidth: 200,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: '0.85rem', color: colors.gray[700] }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}>
        Set by {support.supersededBy}.
      </Typography>
    </Box>
  );
};

export interface PromptEditorProps {
  configKey: ReasoningConfigKey;
  displayName: string;
  description: string;
  activeRow: ReasoningConfigRow | null;
  buffer: ReasoningEditorBuffer;
  onBufferChange: (patch: Partial<ReasoningEditorBuffer>) => void;
  isDirty: boolean;
  saving: boolean;
  onRequestSave: () => void;
  onOpenHistory: () => void;
}

const PromptEditor: React.FC<PromptEditorProps> = ({
  configKey,
  displayName,
  description,
  activeRow,
  buffer,
  onBufferChange,
  isDirty,
  saving,
  onRequestSave,
  onOpenHistory,
}) => {
  const canSave = isDirty && buffer.edit_note.trim().length > 0 && !saving;
  // Which of the three secondary fields the engine actually honours for THIS key.
  const fields = fieldsForKey(configKey);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }} data-testid={`prompt-editor-${configKey}`}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.25rem', fontWeight: 700, color: colors.gray[900] }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{description}</Typography>
        </Box>
        <Button startIcon={<HistoryIcon />} onClick={onOpenHistory} variant="outlined" size="small" sx={{ textTransform: 'none', flexShrink: 0 }}>
          History
        </Button>
      </Box>

      {!activeRow && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No versions yet — saving creates version 1.
        </Alert>
      )}

      <PromptField
        label="Prompt body"
        value={buffer.prompt_body}
        onChange={(v) => onBufferChange({ prompt_body: v })}
        accentColor={ACCENT}
        minRows={16}
        placeholder="Enter the prompt body sent to the model…"
        helperText="{query} is replaced with the member's message at runtime."
      />

      {/* F9: only the fields the engine reads FOR THIS KEY are editable. A
          superseded field becomes a pointer to where the value really comes
          from; an ignored one is not rendered at all (an absent control cannot
          imply it would work). */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {fields.model.support === 'read' && (
          <TextField
            label="Model"
            size="small"
            value={buffer.model}
            onChange={(e) => onBufferChange({ model: e.target.value })}
            sx={{ minWidth: 200 }}
          />
        )}
        <SupersededField label="Model" value={buffer.model || '—'} support={fields.model} />

        {fields.temperature.support === 'read' && (
          <TextField
            label="Temperature"
            size="small"
            type="number"
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            value={buffer.temperature}
            onChange={(e) => onBufferChange({ temperature: e.target.value === '' ? 0 : Number(e.target.value) })}
            sx={{ width: 140 }}
          />
        )}
        <SupersededField label="Temperature" value={buffer.temperature} support={fields.temperature} />

        {fields.max_tokens.support === 'read' && (
          <TextField
            label="Max tokens"
            size="small"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={buffer.max_tokens}
            onChange={(e) => onBufferChange({ max_tokens: e.target.value === '' ? 0 : Number(e.target.value) })}
            sx={{ width: 140 }}
          />
        )}
        <SupersededField label="Max tokens" value={buffer.max_tokens} support={fields.max_tokens} />
      </Box>

      {/* One honest sentence when this key reads NOTHING but the prompt body,
          so the missing inputs read as a fact rather than as a bug. */}
      {fields.model.support !== 'read'
        && fields.temperature.support !== 'read'
        && fields.max_tokens.support !== 'read' && (
        <Typography
          variant="caption"
          data-testid={`prompt-only-note-${configKey}`}
          sx={{ display: 'block', mt: 1, color: 'text.disabled' }}
        >
          For this key the engine reads the prompt body only — the model comes from the Pipeline
          Models page, and temperature and token budget are set in code.
        </Typography>
      )}

      <TextField
        label="What changed and why (required)"
        placeholder="e.g. Tightened the off-mission boundary around medical advice"
        value={buffer.edit_note}
        onChange={(e) => onBufferChange({ edit_note: e.target.value })}
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!canSave}
          onClick={onRequestSave}
          sx={{
            textTransform: 'none',
            backgroundColor: colors.warmGold[500],
            color: colors.gray[900],
            '&:hover': { backgroundColor: colors.warmGold[600] },
          }}
        >
          {saving ? 'Saving…' : 'Save version'}
        </Button>
      </Box>
    </Box>
  );
};

export default PromptEditor;
